import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiUpload } from "@vimaana/ui";
import type {
  AbschlagAbsetzung,
  CatalogBlock,
  CatalogPosition,
  DraftFromInvoiceModus,
  DraftFromInvoiceResult,
  InvoiceCreate,
  InvoiceCreated,
  InvoiceEditorOptions,
  InvoiceFinalizeResult,
  InvoiceImportResult,
  InvoiceKind,
  InvoicePatch,
  InvoiceDocument,
  InvoiceRegister,
  BestaetigungFolgen,
  InvoiceRegisterItem,
  NummerPruefung,
  RegisterAbfrage,
  SnippetListe,
  WebInvoiceDraft,
  WebInvoiceDraftRecord,
} from "../lib/invoices";

export const REGISTER_SEITE = 40;

/**
 * Das Register, seitenweise nachgeladen.
 *
 * Vorher kam der ganze Bestand mit Aktivitätsspur und Dokument-JSON in einem
 * Stück — 400 KB, eine Sekunde, bevor die erste Zeile stand (Benjamin,
 * 02.09.2026). Jetzt liefert der Server 40 Zeilen je Seite; Suche und Reiter
 * gehen als Parameter mit und greifen über ALLE Belege, nicht nur die
 * geladenen. `total` sagt, ob noch etwas folgt.
 */
export function useInvoiceRegister(abfrage: RegisterAbfrage = {}) {
  const params = new URLSearchParams();
  if (abfrage.q) params.set("q", abfrage.q);
  if (abfrage.filter && abfrage.filter !== "all") params.set("filter", abfrage.filter);
  if (abfrage.kunde) params.set("kunde", abfrage.kunde);
  if (abfrage.projekt) params.set("projekt", abfrage.projekt);
  params.set("limit", String(REGISTER_SEITE));
  return useInfiniteQuery<InvoiceRegister>({
    queryKey: ["invoices", "register", abfrage.q || "", abfrage.filter || "all", abfrage.kunde || "", abfrage.projekt || ""],
    queryFn: ({ pageParam }) => {
      const seite = new URLSearchParams(params);
      seite.set("offset", String(pageParam ?? 0));
      return apiGet<InvoiceRegister>(`/api/kontor/rechnungen?${seite.toString()}`);
    },
    initialPageParam: 0,
    getNextPageParam: (letzte) => {
      const naechster = letzte.offset + letzte.items.length;
      return naechster < letzte.total && letzte.items.length > 0 ? naechster : undefined;
    },
    staleTime: 30_000,
  });
}

/** Alle bisher geladenen Zeilen in Belegreihenfolge. */
export const registerZeilen = (data?: { pages: InvoiceRegister[] }) => (
  data?.pages.flatMap((seite) => seite.items) ?? []
);

/** Ein Beleg in voller Tiefe — mit Aktivitätsspur und Dokument-JSON. */
export function useInvoiceDetail(id?: string) {
  return useQuery<InvoiceRegisterItem>({
    queryKey: ["invoices", "detail", id],
    queryFn: () => apiGet<InvoiceRegisterItem>(`/api/kontor/rechnungen/${id}`),
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

export function useCreateInvoiceRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InvoiceCreate) => apiPost<InvoiceCreated>("/api/kontor/rechnungen", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      // Der Auftragswert des Projekts hängt an diesem Beleg — Projektliste und
      // Projektdetail zeigen sonst weiter den Stand von vorher.
      queryClient.invalidateQueries({ queryKey: ["projekte"] });
    },
  });
}

/**
 * Die Belegdatei zu einem Registereintrag nachreichen (PDF oder Foto).
 *
 * Gebraucht für „Externen Beleg erfassen": ohne Datei bleibt der Eintrag eine
 * Zeile mit Zahlen — nicht ansehbar, nicht verschickbar. Ein Foto wandelt der
 * Server in PDF.
 */
export function useUploadInvoicePdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datei }: { id: string; datei: File }) => {
      const form = new FormData();
      form.append("datei", datei);
      return apiUpload<InvoiceRegisterItem>(`/api/kontor/rechnungen/${id}/pdf`, form);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

/**
 * Ein vorhandenes Angebot oder eine Rechnung einlesen lassen.
 *
 * Der Unterschied zu `useUploadInvoicePdf`: dort wird das Papier *angehängt*,
 * hier wird es *gelesen*. Heraus kommt ein Web-Entwurf mit echten Positionen,
 * den der Editor öffnet — kein Registereintrag, nichts Festgeschriebenes.
 *
 * Der Vision-Aufruf dauert je nach Seitenzahl bis zu anderthalb Minuten; die
 * Oberfläche muss das aushalten, ohne dass jemand zweimal klickt.
 */
export function useImportInvoiceKi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (datei: File) => {
      const form = new FormData();
      form.append("datei", datei);
      return apiUpload<InvoiceImportResult>("/api/kontor/rechnungen/import-ki", form);
    },
    onSuccess: (record) => {
      queryClient.setQueryData(["invoices", "draft", record.id], record);
      queryClient.invalidateQueries({ queryKey: ["invoices", "register"] });
    },
  });
}

/**
 * Diesen Ausgangsbeleg an DATEV schicken — auf Knopfdruck.
 *
 * Der Automatikpfad der Eingangsbelege (`scanflow.datev_nachziehen`) hat hier
 * bewusst kein Gegenstück: eine Ausgangsrechnung entsteht oft in mehreren
 * Anläufen, und eine verschickte Mail holt man nicht zurück.
 */
export function useRechnungAnDatev() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<InvoiceRegisterItem>(`/api/kontor/rechnungen/${id}/datev`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}

export function useUpdateInvoiceRecord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: InvoicePatch }) => (
      apiPatch<InvoiceRegisterItem & { bestaetigung_folgen?: BestaetigungFolgen | null }>(
        `/api/kontor/rechnungen/${id}`, patch,
      )
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

export function useInvoiceEditorOptions() {
  return useQuery<InvoiceEditorOptions>({
    queryKey: ["invoices", "editor-options"],
    queryFn: () => apiGet<InvoiceEditorOptions>("/api/kontor/rechnungen/editor/options"),
    staleTime: 10 * 60_000,
  });
}

export function useInvoiceNumber(kind: InvoiceKind) {
  return useQuery<{ number: string; default_payment_term_days: number }>({
    queryKey: ["invoices", "number", kind],
    queryFn: () => apiGet(`/api/kontor/rechnungen/editor/number?kind=${kind}`),
    staleTime: 10_000,
  });
}

/**
 * Das gespeicherte Dokument eines Belegs — die Vorlage zum Bearbeiten.
 *
 * Eigener Endpunkt, nicht die Registerzeile: `/rechnungen` liefert
 * `web_document_json` für alles außer Angeboten bewusst NICHT mit, sonst
 * trüge jede Zeile der Liste ein ganzes Dokument.
 */
export function useInvoiceDocument(id?: string) {
  return useQuery<InvoiceDocument>({
    queryKey: ["invoices", "dokument", id],
    queryFn: () => apiGet<InvoiceDocument>(`/api/kontor/rechnungen/${id}/dokument`),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

/**
 * Einen festgeschriebenen Beleg mit geändertem Inhalt neu erzeugen.
 *
 * Die Nummer bleibt; PDF und ZUGFeRD-XML entstehen neu, und der Server
 * schreibt „Rechnung geändert — <Felder>" in den Verlauf. Auftragswert und
 * Abschlagsverknüpfungen rechnet er mit, deshalb auch die Projektlisten
 * verwerfen.
 */
export function useUpdateFinalizedInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: WebInvoiceDraft }) => (
      apiPost<InvoiceRegisterItem & { aenderungen?: string[] }>(
        `/api/kontor/rechnungen/${id}/neu-schreiben`, draft,
      )
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["projekte"] });
    },
  });
}

/**
 * Beim Tippen fragen, ob eine von Hand gesetzte Belegnummer benutzbar ist.
 *
 * Bewusst nur eine Auskunft, keine Reservierung — vergeben wird die Nummer
 * erst beim Festschreiben, und zwar dort in einer Transaktion. Die Abfrage
 * läuft nur bei eingeschalteter Handvergabe (`enabled`), damit der Normalfall
 * keine zusätzliche Runde zum Server macht.
 */
export function useBelegnummerPruefung(nummer: string, kind: InvoiceKind, enabled: boolean) {
  return useQuery<NummerPruefung>({
    queryKey: ["invoices", "nummer-pruefen", kind, nummer],
    queryFn: () => apiGet<NummerPruefung>(
      `/api/kontor/rechnungen/nummer-pruefen?kind=${kind}&nummer=${encodeURIComponent(nummer)}`,
    ),
    enabled: enabled && nummer.trim().length > 0,
    staleTime: 5_000,
  });
}

/**
 * Die Nummer eines schon festgeschriebenen Belegs richtigstellen.
 *
 * Für den Parallelbetrieb mit Lexware: die Kollision fällt oft erst auf,
 * wenn das Dokument steht. Der Server rendert es mit der neuen Nummer neu und
 * schreibt die Änderung ins Protokoll; versendete oder bezahlte Belege lehnt
 * er ab.
 */
export function useKorrigiereBelegnummer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, nummer, grund }: { id: string; nummer: string; grund?: string }) => (
      apiPost<InvoiceRegisterItem>(`/api/kontor/rechnungen/${id}/nummer`, { nummer, grund: grund || "" })
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["projekte"] });
    },
  });
}

/**
 * Positionen aus der Bibliothek.
 *
 * `aktiv=false` hält die Abfrage an — das Titelfeld sucht erst ab zwei
 * Zeichen, und ohne die Bremse lüde jede leere Zeile beim Fokussieren den
 * halben Katalog.
 */
export function useInvoiceCatalog(query: string, aktiv = true) {
  return useQuery<{ items: CatalogPosition[] }>({
    queryKey: ["invoices", "catalog", query],
    queryFn: () => apiGet(`/api/kontor/rechnungen/editor/positions?q=${encodeURIComponent(query)}&limit=20`),
    enabled: aktiv,
    staleTime: 5 * 60_000,
  });
}

export function useWebInvoiceDraft(id?: string) {
  return useQuery<WebInvoiceDraftRecord>({
    queryKey: ["invoices", "draft", id],
    queryFn: () => apiGet(`/api/kontor/rechnungen/drafts/${id}`),
    enabled: Boolean(id),
    staleTime: 10_000,
  });
}

export function useSaveWebInvoiceDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, draft }: { id?: string; draft: WebInvoiceDraft }) => id
      ? apiPatch<WebInvoiceDraftRecord>(`/api/kontor/rechnungen/drafts/${id}`, draft)
      : apiPost<WebInvoiceDraftRecord>("/api/kontor/rechnungen/drafts", draft),
    onSuccess: (record) => {
      queryClient.setQueryData(["invoices", "draft", record.id], record);
      queryClient.invalidateQueries({ queryKey: ["invoices", "register"] });
    },
  });
}

export function useDeleteWebInvoiceDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: boolean }>(`/api/kontor/rechnungen/drafts/${id}`),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["invoices", "draft", id] });
      queryClient.invalidateQueries({ queryKey: ["invoices", "register"] });
    },
  });
}

/** Position aus dem Editor in den gemeinsamen Katalog übernehmen. */
export function useSaveCatalogPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (position: CatalogPosition) => (
      apiPost<CatalogPosition>("/api/kontor/rechnungen/editor/positions", position)
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices", "catalog"] }),
  });
}

/**
 * Textbaustein aus dem Editor sichern — anlegen oder aktualisieren.
 *
 * Eigener schmaler Endpunkt, NICHT der Voll-PUT der Einstellungen: der Editor
 * kennt immer nur die Liste einer Belegart, ein Voll-Speichern von dort
 * löschte die drei anderen. Der Name ist der Schlüssel; ein vorhandener wird
 * ersetzt, ein neuer angelegt.
 *
 * Danach werden die Editor-Optionen neu geladen, damit der frisch gesicherte
 * Baustein sofort in der Auswahl steht — sonst müsste man die Seite neu laden,
 * um das eigene Werk zu sehen.
 */
export function useSaveTextSnippet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (baustein: { liste: SnippetListe; label: string; text: string }) => (
      apiPost<{ liste: string; label: string; ersetzt: boolean }>(
        "/api/kontor/rechnungen/editor/textbaustein", baustein,
      )
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices", "editor-options"] }),
  });
}

/**
 * Position aus der Bibliothek entfernen — nie aus einem Beleg.
 *
 * Der Titel ist der Schlüssel, wie beim Schreiben. Der Beleg hält seine
 * Zeilen als eigene Kopie, eine Löschung im Katalog wirkt deshalb nur auf
 * künftige Treffer, nicht rückwirkend auf bereits übernommene Positionen.
 */
export function useDeleteCatalogPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (titel: string) => (
      apiDelete<{ ok: boolean }>(`/api/kontor/rechnungen/editor/positions?titel=${encodeURIComponent(titel)}`)
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices", "catalog"] }),
  });
}

/** Bausteine der Bibliothek — Überschrift plus fertige Positionsliste. */
export function useInvoiceBlocks(query: string, aktiv = true) {
  return useQuery<{ items: CatalogBlock[] }>({
    queryKey: ["invoices", "blocks", query],
    queryFn: () => apiGet(`/api/kontor/rechnungen/editor/bloecke?q=${encodeURIComponent(query)}&limit=20`),
    enabled: aktiv,
    staleTime: 5 * 60_000,
  });
}

/** Block aus dem Editor in die Bibliothek übernehmen. */
export function useSaveCatalogBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (block: CatalogBlock) => (
      apiPost<CatalogBlock>("/api/kontor/rechnungen/editor/bloecke", block)
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices", "blocks"] }),
  });
}

/** Block aus der Bibliothek entfernen — nie aus einem Beleg. */
export function useDeleteCatalogBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (titel: string) => (
      apiDelete<{ ok: boolean }>(`/api/kontor/rechnungen/editor/bloecke?titel=${encodeURIComponent(titel)}`)
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices", "blocks"] }),
  });
}

/** Absetzbare Abschlagsrechnungen eines Projekts für die Schlussrechnung. */
export function useAbschlaegeForProjekt(projektId: string) {
  return useQuery<{ items: AbschlagAbsetzung[] }>({
    queryKey: ["invoices", "abschlaege", projektId],
    queryFn: () => apiGet(`/api/kontor/rechnungen/abschlaege?projekt_id=${encodeURIComponent(projektId)}`),
    enabled: Boolean(projektId),
    staleTime: 30_000,
  });
}

/** Kopie, Angebot→Rechnung oder Storno als neuen Entwurf anlegen. */
export function useDraftFromInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, modus, anteileProzent = [], abschlagProzent, abschlagNetto }: {
      id: string; modus: DraftFromInvoiceModus; anteileProzent?: number[];
      /** Abschlag auf die Gesamtsumme: Prozent ODER Nettobetrag. */
      abschlagProzent?: number; abschlagNetto?: number;
    }) => (
      apiPost<DraftFromInvoiceResult>(`/api/kontor/rechnungen/${id}/entwurf`, {
        modus,
        anteile_prozent: anteileProzent,
        ...(abschlagProzent ? { abschlag_prozent: abschlagProzent } : {}),
        ...(abschlagNetto ? { abschlag_netto: abschlagNetto } : {}),
      })
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invoices", "register"] }),
  });
}

export function useFinalizeInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, draft }: { draftId?: string; draft: WebInvoiceDraft }) => (
      apiPost<InvoiceFinalizeResult>("/api/kontor/rechnungen/finalize", {
        draft_id: draftId || null,
        draft,
      })
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}

export async function previewInvoicePdf(draft: WebInvoiceDraft) {
  const response = await fetch("/api/kontor/rechnungen/preview", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(draft),
  });
  if (!response.ok) {
    let message = `PDF-Vorschau fehlgeschlagen (${response.status}).`;
    try {
      const body = await response.json() as { detail?: string };
      if (body.detail) message = body.detail;
    } catch { /* Text-Fallback bleibt bestehen. */ }
    throw new Error(message);
  }
  return {
    blob: await response.blob(),
    number: response.headers.get("X-Vimaana-Preview-Number") || draft.doc_number,
  };
}

/**
 * Wie ein angenommenes Angebot zu einem anderen am Projekt gehört (Migration 222):
 * ersetzt / enthalten (zählen nicht) oder zusätzlich (zählt). `rolle: null` nimmt es zurück.
 */
export function useAngebotRolle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; rolle: "ersetzt" | "enthalten" | "zusaetzlich" | null; zu: string }) =>
      apiPost<{ ok: boolean; auftragswert: number }>(`/api/kontor/rechnungen/${v.id}/angebot-rolle`, { rolle: v.rolle, zu: v.zu }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projekt"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}
